/**
 * What one renderer can tell the others about the state of the picture.
 *
 * Phase 6C needs two materials to interact without knowing about each other:
 * code has to appear only where ink has been laid, glyphs have to drift in the
 * direction smoke is moving, waves have to start from the edge of a brush mark.
 * The obvious way to do that is for one renderer to import another, and it is
 * the wrong way — it makes the pair a unit, it means a build without smoke
 * cannot have code, and it puts three's scene graph in the middle of what is
 * really a question about the artwork.
 *
 * So a renderer may *publish* a reading of itself, and any renderer may *ask*.
 * Neither knows who is on the other end. A material that publishes nothing is
 * still perfectly usable; a material that asks and finds nothing gets zeroes and
 * carries on, which is exactly what should happen in a recipe that did not
 * include the other half.
 *
 * This is deliberately not a compositor. There is no render graph here, no
 * texture sharing, no ordering contract — just a handful of scalars sampled at a
 * point. Anything a renderer cannot say in those terms it keeps to itself.
 *
 * Coordinates are field units throughout: 1 is the playfield radius, (0, 0) is
 * the centre, exactly as in `WaveEvent`.
 */

export interface VisualFieldSample {
  /** 0..1. How much of this point is covered by something opaque. */
  readonly coverage: number;
  /** 0..1. How much material is here, which may exceed what is visible. */
  readonly density: number;
  /** 0..1. How much of it is still moving. */
  readonly wetness: number;
  /** Linear RGB of whatever is here. Black when nothing is. */
  readonly pigment: readonly [number, number, number];
  /** Field units per second. Which way this point is being carried. */
  readonly flow: readonly [number, number];
}

export const EMPTY_SAMPLE: VisualFieldSample = {
  coverage: 0,
  density: 0,
  wetness: 0,
  pigment: [0, 0, 0],
  flow: [0, 0],
};

/** What a renderer sees when it asks about the picture. */
export interface VisualFieldReader {
  sample(x: number, y: number): VisualFieldSample;
  /** The whole field's mean coverage, for decisions that are not about a point. */
  readonly coverage: number;
  readonly wetness: number;
}

/**
 * Several readers, answered as one.
 *
 * Coverage and density add, because two materials over the same point do cover
 * it more. Wetness takes the maximum rather than the sum: a point is as wet as
 * the wettest thing on it, and adding would make two half-dry marks read as
 * fully wet. Flow adds, because two forces on the same point compose. Pigment is
 * weighted by density, so a heavy mark decides the colour and a faint one only
 * tints it — an unweighted mean would let an almost-invisible layer wash out the
 * one actually on the page.
 */
export class CompositeVisualField implements VisualFieldReader {
  private readonly readers = new Map<string, VisualFieldReader>();

  publish(id: string, reader: VisualFieldReader): void {
    this.readers.set(id, reader);
  }

  withdraw(id: string): void {
    this.readers.delete(id);
  }

  clear(): void {
    this.readers.clear();
  }

  get providers(): readonly string[] {
    return [...this.readers.keys()];
  }

  sample(x: number, y: number): VisualFieldSample {
    if (this.readers.size === 0) return EMPTY_SAMPLE;

    let coverage = 0;
    let density = 0;
    let wetness = 0;
    let flowX = 0;
    let flowY = 0;
    let red = 0;
    let green = 0;
    let blue = 0;
    let weight = 0;

    for (const reader of this.readers.values()) {
      const sample = reader.sample(x, y);
      coverage += sample.coverage;
      density += sample.density;
      wetness = Math.max(wetness, sample.wetness);
      flowX += sample.flow[0];
      flowY += sample.flow[1];
      red += sample.pigment[0] * sample.density;
      green += sample.pigment[1] * sample.density;
      blue += sample.pigment[2] * sample.density;
      weight += sample.density;
    }

    const scale = weight > 1e-6 ? 1 / weight : 0;
    return {
      coverage: clamp01(coverage),
      density,
      wetness: clamp01(wetness),
      pigment: [red * scale, green * scale, blue * scale],
      flow: [flowX, flowY],
    };
  }

  get coverage(): number {
    let total = 0;
    for (const reader of this.readers.values()) total += reader.coverage;
    return clamp01(total);
  }

  get wetness(): number {
    let most = 0;
    for (const reader of this.readers.values()) most = Math.max(most, reader.wetness);
    return clamp01(most);
  }
}

/**
 * A coverage map on a coarse grid.
 *
 * The obvious implementation of "where has paint been laid" is to read the
 * texture the paint was drawn into, and it is unusable: a GPU readback stalls
 * the pipeline for longer than the frame it is trying to inform, and an offline
 * re-render would have to do it for every one of ten thousand frames.
 *
 * So the grid is maintained on the CPU, from the same list of marks that is
 * drawn on the GPU. It cannot drift from the picture, because both are written
 * by the same call. It is coarse on purpose — this answers "is there ink around
 * here", which is a question about regions, and a grid fine enough to answer it
 * per pixel would cost more than the paint does.
 */
export class PaintFieldGrid implements VisualFieldReader {
  private readonly size: number;
  private readonly density: Float32Array;
  private readonly wet: Float32Array;
  private readonly red: Float32Array;
  private readonly green: Float32Array;
  private readonly blue: Float32Array;
  /** Field units covered by the grid: -extent..+extent on both axes. */
  private readonly extent: number;
  private deposits = 0;

  constructor(options: { readonly size?: number; readonly extent?: number } = {}) {
    this.size = Math.max(8, Math.min(128, options.size ?? 48));
    this.extent = options.extent ?? 1.1;
    const cells = this.size * this.size;
    this.density = new Float32Array(cells);
    this.wet = new Float32Array(cells);
    this.red = new Float32Array(cells);
    this.green = new Float32Array(cells);
    this.blue = new Float32Array(cells);
  }

  clear(): void {
    this.density.fill(0);
    this.wet.fill(0);
    this.red.fill(0);
    this.green.fill(0);
    this.blue.fill(0);
    this.deposits = 0;
  }

  /** Lays material down over a disc, in field units. */
  deposit(options: {
    readonly x: number;
    readonly y: number;
    readonly radius: number;
    readonly density: number;
    readonly wetness: number;
    readonly pigment: readonly [number, number, number];
  }): void {
    const step = (this.extent * 2) / this.size;
    const radius = Math.max(options.radius, step * 0.5);
    const minX = this.cellOf(options.x - radius);
    const maxX = this.cellOf(options.x + radius);
    const minY = this.cellOf(options.y - radius);
    const maxY = this.cellOf(options.y + radius);

    for (let gy = minY; gy <= maxY; gy += 1) {
      for (let gx = minX; gx <= maxX; gx += 1) {
        const worldX = -this.extent + (gx + 0.5) * step;
        const worldY = -this.extent + (gy + 0.5) * step;
        const distance = Math.hypot(worldX - options.x, worldY - options.y);
        if (distance > radius) continue;
        // Falls off towards the rim, so a mark's edge reads as an edge rather
        // than as a cliff — the renderers that read this use it to decide where
        // something is *mostly* covered.
        const weight = 1 - (distance / radius) * 0.75;
        const index = gy * this.size + gx;
        const added = options.density * weight;
        this.density[index] = Math.min(4, (this.density[index] as number) + added);
        this.wet[index] = Math.max(this.wet[index] as number, options.wetness);
        this.red[index] = (this.red[index] as number) + options.pigment[0] * added;
        this.green[index] = (this.green[index] as number) + options.pigment[1] * added;
        this.blue[index] = (this.blue[index] as number) + options.pigment[2] * added;
      }
    }
    this.deposits += 1;
  }

  /** Time passes: what is wet dries, and what is dry may slowly fade. */
  advance(deltaSeconds: number, options: { readonly drying: number; readonly persistence: number }): void {
    const dryStep = Math.exp(-Math.max(0, options.drying) * deltaSeconds);
    // Persistence 1 means the stain never leaves within a run, which is the
    // default: this is an artwork accumulating, not an effect decaying.
    const keep =
      options.persistence >= 1
        ? 1
        : Math.exp(-Math.max(0, 1 - options.persistence) * 0.06 * deltaSeconds);
    for (let index = 0; index < this.wet.length; index += 1) {
      this.wet[index] = (this.wet[index] as number) * dryStep;
      if (keep < 1) {
        this.density[index] = (this.density[index] as number) * keep;
        this.red[index] = (this.red[index] as number) * keep;
        this.green[index] = (this.green[index] as number) * keep;
        this.blue[index] = (this.blue[index] as number) * keep;
      }
    }
  }

  sample(x: number, y: number): VisualFieldSample {
    const index = this.indexOf(x, y);
    if (index < 0) return EMPTY_SAMPLE;
    const density = this.density[index] as number;
    if (density <= 1e-5) return EMPTY_SAMPLE;
    const scale = 1 / density;
    return {
      coverage: Math.min(1, density),
      density,
      wetness: this.wet[index] as number,
      pigment: [
        (this.red[index] as number) * scale,
        (this.green[index] as number) * scale,
        (this.blue[index] as number) * scale,
      ],
      flow: [0, 0],
    };
  }

  /** Mean coverage over the grid. What fraction of the picture has been painted. */
  get coverage(): number {
    let total = 0;
    for (let index = 0; index < this.density.length; index += 1) {
      total += Math.min(1, this.density[index] as number);
    }
    return total / this.density.length;
  }

  get wetness(): number {
    let most = 0;
    for (let index = 0; index < this.wet.length; index += 1) {
      most = Math.max(most, this.wet[index] as number);
    }
    return most;
  }

  diagnostics(): {
    readonly size: number;
    readonly deposits: number;
    readonly coverage: number;
    readonly wetness: number;
    /** Fraction of cells with any material at all. */
    readonly touched: number;
  } {
    let touched = 0;
    for (let index = 0; index < this.density.length; index += 1) {
      if ((this.density[index] as number) > 0.02) touched += 1;
    }
    return {
      size: this.size,
      deposits: this.deposits,
      coverage: Number(this.coverage.toFixed(4)),
      wetness: Number(this.wetness.toFixed(4)),
      touched: Number((touched / this.density.length).toFixed(4)),
    };
  }

  private cellOf(value: number): number {
    const step = (this.extent * 2) / this.size;
    const cell = Math.floor((value + this.extent) / step);
    return cell < 0 ? 0 : cell >= this.size ? this.size - 1 : cell;
  }

  private indexOf(x: number, y: number): number {
    if (x < -this.extent || x > this.extent || y < -this.extent || y > this.extent) return -1;
    return this.cellOf(y) * this.size + this.cellOf(x);
  }
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
